import React, { FC, ReactNode } from 'react';
import {
  Badge,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  Col,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from 'reactstrap';
import { ProductImage, ProductImageType } from '../../../../../../shared/orders/product';
import { ActionButton, MaterialIcon } from '../../../../../components';
import { useTranslation } from '../../../../../translations';

interface ImageListItemComponentProps {
  readonly productId: number;
  readonly image: ProductImage;
  readonly onUpdate: () => void;
}

export const ImageListItemComponent: FC<ImageListItemComponentProps> = ({
  productId,
  image,
  onUpdate,
}) => {
  const { ts } = useTranslation();
  return (
    <Col id={`image-${image.id}`} lg={3} xs={12}>
      <Card color={image.type === 'primary' ? 'primary' : 'secondary'} outline>
        <CardBody>
          <img src={`/api/products/${productId}/images/${image.id}`} style={{ maxWidth: '100%' }} />
        </CardBody>
        <CardFooter>
          <Row>
            <Col lg={6} xs={12}>
              {image.type === 'primary' && <Badge color="primary">{ts('primary')}</Badge>}
              {image.type === 'image' && <Badge color="info">{ts('image')}</Badge>}
              {image.type === 'badgeArt' && <Badge color="warning">{ts('common.badge_art')}</Badge>}
              {image.type === 'hidden' && <Badge color="secondary">{ts('hidden')}</Badge>}
            </Col>
            <Col lg={6} xs={12}>
              <ImageUpdateButtons image={image} onUpdate={onUpdate} productId={productId} />
            </Col>
          </Row>
        </CardFooter>
      </Card>
    </Col>
  );
};

interface ImageUpdateButtonsProps {
  readonly productId: number;
  readonly image: ProductImage;
  readonly onUpdate: () => void;
}

const ImageUpdateButtons: FC<ImageUpdateButtonsProps> = ({ productId, image, onUpdate }) => {
  const { ts } = useTranslation();
  return (
    <ButtonGroup>
      <UncontrolledDropdown>
        <DropdownToggle caret className="open-menu">
          <MaterialIcon name="settings" />
        </DropdownToggle>
        <DropdownMenu>
          {image.type !== 'primary' && (
            <SetImageType
              assetId={image.id}
              className="setPrimary"
              onUpdate={onUpdate}
              productId={productId}
              type="primary"
            >
              {ts('set_primary')}
            </SetImageType>
          )}
          {image.type !== 'hidden' && (
            <SetImageType
              assetId={image.id}
              className="setHidden"
              onUpdate={onUpdate}
              productId={productId}
              type="hidden"
            >
              {ts('set_hidden')}
            </SetImageType>
          )}
          {image.type !== 'badgeArt' && (
            <SetImageType
              assetId={image.id}
              className="setBadgeArt"
              onUpdate={onUpdate}
              productId={productId}
              type="badgeArt"
            >
              {ts('set')} {ts('common.badge_art')}
            </SetImageType>
          )}
          <ActionButton
            action={async () => {
              await api.deleteProductImage(productId, image.id);
            }}
            block
            className="dropdown-item action-delete"
            color="danger"
            onSuccess={onUpdate}
          >
            {ts('common.delete')}
          </ActionButton>
        </DropdownMenu>
      </UncontrolledDropdown>
    </ButtonGroup>
  );
};

interface SetImageTypeProps {
  readonly children?: ReactNode;
  readonly className: string;
  readonly productId: number;
  readonly assetId: string;
  readonly type: ProductImageType;
  readonly onUpdate: () => void;
}

const SetImageType: FC<SetImageTypeProps> = ({
  productId,
  type,
  assetId,
  onUpdate,
  children,
  className,
}) => {
  return (
    <ActionButton
      action={async () => {
        await api.updateProductImageType(productId, assetId, type);
      }}
      block
      className={`dropdown-item ${className}`}
      color="secondary"
      onSuccess={onUpdate}
      outline
    >
      {children}
    </ActionButton>
  );
};
